import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useGamePreferences, HIGHLIGHT_PRESETS, ANSWER_PRESETS } from '../contexts/GamePreferencesContext';
import { useSudokuGame, removeCandidateFromRelated } from '../hooks/useSudokuGame';
import { useGameTimer } from '../hooks/useGameTimer';
import { useCellSelection } from '../hooks/useCellSelection';
import { useWisdomFact } from '../hooks/useWisdomFact';
import { ScribbleCanvas } from '../components/ScribbleCanvas';
import { AdSlot } from '../components/AdSlot';
import UnlockModal from '../components/UnlockModal';
import { playVictoryRiff, primeAudio } from '../utils/victoryAudio';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  ArrowLeft, 
  Eraser, 
  Pencil, 
  Lightbulb, 
  Pause, 
  Play, 
  RotateCcw,
  Check,
  Share2,
  Focus,
  PenTool,
  Trash2,
  Undo2,
  Redo2,
  Sun,
  Moon,
  Settings2,
  ShieldCheck,
  LifeBuoy,
  X,
  BookOpen,
  ChevronLeft,
  ChevronRight,
  SkipForward,
  Square,
  ZoomIn,
  ZoomOut,
  Pen,
  MoreHorizontal,
  Star
} from 'lucide-react';
import { toast } from 'sonner';
import { PanHint } from '../components/PanHint';
import { IS_ANDROID_APP } from '../utils/platform';
import { StuckDialog } from '../components/StuckDialog';
import { HelpThreadPanel } from '../components/HelpThreadPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Extracted: Sudoku Board Component
const SudokuBoard = React.memo(function SudokuBoard({ 
  grid, 
  originalGrid, 
  pencilMarks, 
  errors, 
  onCellClick, 
  isSelected, 
  isRelated, 
  isSameNumber,
  isUnrelated,
  focusIntensity,
  answerStyle,
  answerColorValue,
  pendingPlacement,
  pendingReferences,
}) {
  const getCellClassName = useCallback((row, col) => {
    const classes = ['sudoku-cell'];
    if (originalGrid[row][col] !== 0) classes.push('clue-cell');
    if (isSelected(row, col)) classes.push('selected');
    if (isRelated(row, col)) classes.push('related');
    if (isSameNumber(row, col)) classes.push('same-number');
    if (errors.some(([r, c]) => r === row && c === col)) classes.push('error-cell');
    if (isUnrelated(row, col)) classes.push('unfocused');
    if (pendingPlacement && pendingPlacement.row === row && pendingPlacement.col === col) {
      classes.push('walkthrough-pending');
    }
    // Pattern roles for advanced techniques (XY-Wing pivot/wing,
    // X-Wing corner). Distinct shading so the user can see WHICH cells
    // form the pattern, separate from the cells where eliminations happen.
    if (pendingReferences) {
      const ref = pendingReferences.find((r) => r.row === row && r.col === col);
      if (ref) classes.push(`pattern-${ref.role}`);
    }
    return classes.join(' ');
  }, [isSelected, isRelated, isSameNumber, isUnrelated, errors, originalGrid, pendingPlacement, pendingReferences]);

  const getCellTextColor = useCallback((row, col) => {
    if (originalGrid[row][col] !== 0) return 'text-[var(--text-primary)]';
    if (errors.some(([r, c]) => r === row && c === col)) return 'text-[#FF3B30]';
    // Use custom answer color
    if (answerStyle === 'dark') return 'text-[var(--text-primary)]';
    if (answerColorValue) return '';
    return 'text-[#5AC8FA]';
  }, [originalGrid, errors, answerStyle, answerColorValue]);

  const getAnswerInlineStyle = useCallback((row, col) => {
    if (originalGrid[row][col] !== 0) return {};
    if (errors.some(([r, c]) => r === row && c === col)) return {};
    if (answerStyle === 'adaptive') {
      return { color: 'var(--text-primary)', opacity: 0.85 };
    }
    if (answerStyle === 'dark') return {};
    if (answerColorValue) return { color: answerColorValue };
    return {};
  }, [originalGrid, errors, answerStyle, answerColorValue]);

  const renderCellContent = useCallback((cell, rowIndex, colIndex) => {
    if (cell !== 0) {
      const isUserEntry = originalGrid[rowIndex][colIndex] === 0;
      return (
        <span 
          className={`digit-answer ${getCellTextColor(rowIndex, colIndex)} animate-cell-pop`}
          style={isUserEntry ? getAnswerInlineStyle(rowIndex, colIndex) : {}}
        >
          {cell}
        </span>
      );
    }
    // Walkthrough preview: cell is empty BUT this step would place a digit
    // here on Next. Keep the existing pencil marks visible (so the user can
    // read the candidates in context) AND highlight THIS digit's slot with
    // an amber blink + color so it stands out as "the one about to land."
    // The candidate stays in its correct spatial position within the 3x3
    // candidate grid — no circle, no displacement.
    const pendingValue =
      pendingPlacement &&
      pendingPlacement.row === rowIndex &&
      pendingPlacement.col === colIndex
        ? pendingPlacement.value
        : null;
    const cellPencilMarks = pencilMarks[rowIndex]?.[colIndex] || [];
    if (pendingValue || cellPencilMarks.length > 0) {
      return (
        <div className="pencil-marks">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => {
            const isPending = num === pendingValue;
            const inCellMarks = cellPencilMarks.includes(num);
            // Render if the user has this pencil mark OR if it's the
            // pending candidate for the walkthrough (so it stays visible
            // even when the user hadn't jotted it down themselves).
            const shown = inCellMarks || isPending;
            return (
              <span
                key={num}
                className={`pencil-mark ${isPending ? 'pencil-mark-pending' : ''}`}
                data-testid={isPending ? `pending-digit-${rowIndex}-${colIndex}` : undefined}
              >
                {shown ? num : ''}
              </span>
            );
          })}
        </div>
      );
    }
    return null;
  }, [getCellTextColor, getAnswerInlineStyle, pencilMarks, originalGrid, pendingPlacement]);

  return (
    <div 
      className="sudoku-board" 
      data-testid="sudoku-board"
      style={{ '--focus-dim': focusIntensity }}
    >
      {grid.map((row, rowIndex) =>
        row.map((cell, colIndex) => (
          <div
            key={`${rowIndex}-${colIndex}`}
            className={getCellClassName(rowIndex, colIndex)}
            onClick={() => onCellClick(rowIndex, colIndex)}
            data-testid={`cell-${rowIndex}-${colIndex}`}
          >
            {renderCellContent(cell, rowIndex, colIndex)}
          </div>
        ))
      )}
    </div>
  );
});

// Numpad digit renderer — in Ink Pen mode, shows the digit centered & full size.
// In Pencil mode, shows it small and positioned within a 3×3 grid that
// matches the digit's candidate location in a Sudoku cell:
//   1 2 3
//   4 5 6
//   7 8 9
// User benefit: spatial muscle-memory — wherever the digit lives on the
// numpad button is exactly where the pencil mark will appear in the cell.
const NumpadDigit = React.memo(function NumpadDigit({ value, pencilMode }) {
  if (!pencilMode) {
    return <span className="numpad-digit-full">{value}</span>;
  }
  const positions = {
    1: 'tl', 2: 'tc', 3: 'tr',
    4: 'ml', 5: 'mc', 6: 'mr',
    7: 'bl', 8: 'bc', 9: 'br',
  };
  return (
    <span className={`numpad-digit-pencil pos-${positions[value]}`}>{value}</span>
  );
});

// Extracted: Numpad Component — Compact icon-based layout
const Numpad = React.memo(function Numpad({ 
  onNumberClick, 
  onErase, 
  onToggleNotes, 
  onHint, 
  onAutoFill,
  onUndo,
  onRedo,
  onToggleScribble,
  scribbleActive = false,
  scribbleEraserActive = false,
  noteMode 
}) {
  // Embedded pen/pencil button (for mobile portrait + landscape numpads).
  // Visually matches a numpad key but tinted by mode.
  const ModeKey = (
    <button
      className={`numpad-btn numpad-mode-key ${noteMode ? 'mode-pencil' : 'mode-pen'}`}
      onClick={onToggleNotes}
      data-testid="toggle-notes-btn"
      aria-pressed={noteMode}
      aria-label={noteMode ? 'Switch to Ink Pen (Answers)' : 'Switch to Pencil (Candidates)'}
      title={noteMode ? 'Switch to Ink Pen (Answers)' : 'Switch to Pencil (Candidates)'}
    >
      {noteMode ? <Pencil className="w-5 h-5" /> : <Pen className="w-5 h-5" />}
    </button>
  );

  return (
    <Card className="bg-[var(--background-surface)] border-[var(--border-subtle)] numpad-card">
      {/* Desktop-only: full-width Pen/Pencil header (large readable badge) */}
      <button
        className={`numpad-mode-toggle numpad-mode-toggle-desktop ${noteMode ? 'mode-pencil' : 'mode-pen'}`}
        onClick={onToggleNotes}
        data-testid="toggle-notes-btn-desktop"
        aria-pressed={noteMode}
        title={noteMode ? "Switch to Ink Pen (Answers)" : "Switch to Pencil (Candidates)"}
      >
        {noteMode
          ? <Pencil className="numpad-mode-icon" aria-hidden="true" />
          : <Pen className="numpad-mode-icon" aria-hidden="true" />}
        <span className="numpad-mode-text">
          <span className="numpad-mode-primary">{noteMode ? 'Pencil' : 'Ink Pen'}</span>
          <span className="numpad-mode-secondary">{noteMode ? 'Candidates' : 'Answers'}</span>
        </span>
      </button>

      {/* Portrait mobile: 5×2 layout — 1|2|3|4|Pen / 5|6|7|8|9.
          The bottom action row (Undo/Redo/Hint/Draw) moved OUT of the
          numpad into the two below-grid rows so the numpad card stays
          compact and every digit stays visible above the browser tab bar. */}
      <div className="numpad-portrait">
        <div className="grid grid-cols-5 gap-1.5 mb-1.5">
          {[1, 2, 3, 4].map(num => (
            <button key={num} className="numpad-btn" onClick={() => onNumberClick(num)} data-testid={`numpad-${num}`}>
              <NumpadDigit value={num} pencilMode={noteMode} />
            </button>
          ))}
          {ModeKey}
        </div>
        <div className="grid grid-cols-5 gap-1.5">
          {[5, 6, 7, 8, 9].map(num => (
            <button key={num} className="numpad-btn" onClick={() => onNumberClick(num)} data-testid={`numpad-${num}`}>
              <NumpadDigit value={num} pencilMode={noteMode} />
            </button>
          ))}
        </div>
      </div>

      {/* Landscape mobile: 3×3 digits + full-width Pen row.
          Undo/Redo removed — they now live in the below-numpad rows the
          user requested, so keeping them here was pure duplication and
          cost a full row (~46px) of vertical space we needed for the
          keypad. Pen stays because it's the primary mode switch used
          every move. */}
      <div className="numpad-landscape">
        <div className="grid grid-cols-3 gap-1.5">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
            <button key={num} className="numpad-btn" onClick={() => onNumberClick(num)} data-testid={`numpad-${num}`}>
              <NumpadDigit value={num} pencilMode={noteMode} />
            </button>
          ))}
        </div>
        <button
          onClick={onToggleNotes}
          className={`numpad-mode-key numpad-mode-key-wide ${noteMode ? 'mode-pencil' : 'mode-pen'}`}
          aria-pressed={noteMode}
          aria-label={noteMode ? 'Switch to Ink Pen' : 'Switch to Pencil'}
          title={noteMode ? 'Switch to Ink Pen (answers)' : 'Switch to Pencil (candidates)'}
          data-testid="numpad-mode-toggle"
        >
          {noteMode ? (
            <><Pencil className="w-3.5 h-3.5" /><span>Pencil</span></>
          ) : (
            <><Pen className="w-3.5 h-3.5" /><span>Ink Pen</span></>
          )}
        </button>
      </div>

      {/* Desktop wide (>=1024px): existing 3×3 + 3-button action row (Undo, Redo, Hint) */}
      <div className="numpad-desktop">
        <div className="numpad mb-2">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
            <button key={num} className="numpad-btn" onClick={() => onNumberClick(num)} data-testid={`numpad-${num}`}>
              <NumpadDigit value={num} pencilMode={noteMode} />
            </button>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-1.5">
          <button className="numpad-action-btn labeled" onClick={onUndo} data-testid="undo-btn">
            <Undo2 className="w-4 h-4" aria-hidden="true" />
            <span className="action-label">Undo</span>
          </button>
          <button className="numpad-action-btn labeled" onClick={onRedo} data-testid="redo-btn">
            <Redo2 className="w-4 h-4" aria-hidden="true" />
            <span className="action-label">Redo</span>
          </button>
          <button className="numpad-action-btn labeled" onClick={onHint} data-testid="hint-btn">
            <Lightbulb className="w-4 h-4" aria-hidden="true" />
            <span className="action-label">Hint</span>
          </button>
        </div>
      </div>
    </Card>
  );
});

// Extracted: Game Header Component
const GameHeader = React.memo(function GameHeader({ 
  difficulty, 
  timeDisplay, 
  isPaused, 
  onBack, 
  onTogglePause,
  theme,
  onToggleTheme
}) {
  return (
    <header className="border-b border-[var(--border-subtle)] px-4 py-2 landscape-compact-header">
      <div className="max-w-6xl mx-auto flex items-center justify-between">
        <button 
          onClick={onBack}
          className="flex items-center gap-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Back</span>
        </button>
        
        <div className="flex items-center gap-2">
          <button
            onClick={onToggleTheme}
            className="p-2 rounded text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--hover-bg)] transition-colors"
            data-testid="game-theme-toggle"
            aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
          >
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>
          <div className="timer flex items-center gap-2" data-testid="timer">
            <button 
              onClick={onTogglePause}
              className="p-2 rounded hover:bg-[var(--hover-bg)] transition-colors"
              data-testid="pause-btn"
            >
            {isPaused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
          </button>
          <span className="font-mono text-lg">{timeDisplay}</span>
          </div>
        </div>
      </div>
    </header>
  );
});

// Extracted: Completion Card Component
const CompletionCard = React.memo(function CompletionCard({ timeDisplay, onShare, onNewPuzzle }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      data-testid="completion-overlay"
    >
      <Card
        className="bg-[var(--background-surface)] border-[#34C759]/40 p-6 text-center max-w-sm w-full shadow-2xl"
        data-testid="completion-card"
      >
        <div className="w-14 h-14 rounded-full bg-[#34C759]/15 flex items-center justify-center mx-auto mb-3">
          <Check className="w-8 h-8 text-[#34C759]" />
        </div>
        <h3 className="font-heading font-bold text-xl text-[var(--text-primary)] mb-1">
          Puzzle Complete!
        </h3>
        <p className="text-[var(--text-secondary)] mb-5">Time: <span className="font-mono font-semibold">{timeDisplay}</span></p>
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="flex-1 border-[var(--border-medium)]"
            onClick={onShare}
            data-testid="share-btn"
          >
            <Share2 className="w-4 h-4 mr-2" />
            Share
          </Button>
          <Button
            className="flex-1 bg-[#8B5A3C] hover:bg-[#A46944] text-white"
            onClick={onNewPuzzle}
            data-testid="new-puzzle-btn"
          >
            New Puzzle
          </Button>
        </div>
      </Card>
    </div>
  );
});

// Extracted: Paused Overlay Component
const PausedOverlay = React.memo(function PausedOverlay({ onResume }) {
  return (
    <div className="sudoku-board flex items-center justify-center bg-[var(--background-surface)]">
      <div className="text-center">
        <Pause className="w-12 h-12 text-[var(--text-disabled)] mx-auto mb-4" />
        <p className="text-[var(--text-secondary)]">Game Paused</p>
        <Button 
          onClick={onResume}
          className="mt-4 bg-[#8B5A3C] hover:bg-[#A46944]"
          data-testid="resume-btn"
        >
          Resume
        </Button>
      </div>
    </div>
  );
});

const WisdomTidbit = React.memo(function WisdomTidbit({ fact, fading }) {
  if (!fact) return null;
  return (
    <div 
      className={`relative pl-4 ${fading ? 'wisdom-fade-exit' : 'wisdom-fade-enter'}`}
      data-testid="wisdom-tidbit"
    >
      <div className="absolute left-0 top-2 bottom-2 w-px bg-gradient-to-b from-[var(--wisdom-border)] via-[var(--wisdom-border-mid)] to-transparent" />
      <p className="wisdom-text">
        &ldquo;{fact.text}&rdquo;
      </p>
      <p className="wisdom-source">
        — {fact.source}
      </p>
    </div>
  );
});

// Settings Panel Component
const GameSettingsPanel = React.memo(function GameSettingsPanel({ onClose }) {
  const { prefs, updatePref, HIGHLIGHT_PRESETS: hlPresets, ANSWER_PRESETS: ansPresets } = useGamePreferences();

  const colorSwatches = Object.entries(hlPresets);
  const answerOptions = Object.entries(ansPresets);

  return (
    <Card className="bg-[var(--background-surface)] border-[var(--border-subtle)] p-3 animate-slide-up" data-testid="game-settings-panel">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider">Display Settings</span>
        <button onClick={onClose} className="p-1 rounded hover:bg-[var(--hover-bg)] text-[var(--text-muted)]" data-testid="close-settings-btn">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Highlight Style */}
      <div className="mb-3">
        <label className="text-xs text-[var(--text-muted)] mb-1.5 block">Highlight Style</label>
        <div className="flex gap-1.5">
          {['shade', 'outline'].map(style => (
            <button
              key={style}
              onClick={() => updatePref('highlightStyle', style)}
              className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                prefs.highlightStyle === style
                  ? 'bg-[var(--accent-primary)] text-white'
                  : 'bg-[var(--hover-bg-subtle)] text-[var(--text-muted)] border border-[var(--border-subtle)] hover:text-[var(--text-secondary)]'
              }`}
              data-testid={`highlight-style-${style}`}
            >
              {style === 'shade' ? 'Shade' : 'Outline'}
            </button>
          ))}
        </div>
      </div>

      {/* Highlight Color */}
      <div className="mb-3">
        <label className="text-xs text-[var(--text-muted)] mb-1.5 block">Highlight Color</label>
        <div className="flex gap-1.5 flex-wrap">
          {colorSwatches.map(([key, preset]) => (
            <button
              key={key}
              onClick={() => updatePref('highlightColor', key)}
              className={`w-7 h-7 rounded-full transition-all ${
                prefs.highlightColor === key ? 'ring-2 ring-offset-1 ring-[var(--text-primary)] scale-110' : 'hover:scale-105'
              }`}
              style={{ backgroundColor: `rgb(${preset.rgb})`, ringOffsetColor: 'var(--background-surface)' }}
              title={preset.label}
              data-testid={`highlight-color-${key}`}
            />
          ))}
        </div>
      </div>

      {/* Answer Color */}
      <div>
        <label className="text-xs text-[var(--text-muted)] mb-1.5 block">Answer Color</label>
        <div className="flex gap-1 flex-wrap">
          {answerOptions.map(([key, preset]) => (
            <button
              key={key}
              onClick={() => updatePref('answerStyle', key)}
              className={`px-2 py-1 rounded text-xs transition-colors ${
                prefs.answerStyle === key
                  ? 'bg-[var(--accent-primary)] text-white'
                  : 'bg-[var(--hover-bg-subtle)] text-[var(--text-muted)] border border-[var(--border-subtle)] hover:text-[var(--text-secondary)]'
              }`}
              data-testid={`answer-style-${key}`}
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>

      {/* Numpad Side (handedness — only matters in landscape mobile/tablet) */}
      <div className="mt-3">
        <label className="text-xs text-[var(--text-muted)] mb-1.5 block">Numpad Side <span className="text-[var(--text-disabled)] font-normal">(landscape)</span></label>
        <div className="flex gap-1.5">
          {[
            ['left', 'Left-handed'],
            ['right', 'Right-handed'],
          ].map(([key, label]) => (
            <button
              key={key}
              onClick={() => updatePref('numpadSide', key)}
              className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                (prefs.numpadSide || 'right') === key
                  ? 'bg-[var(--accent-primary)] text-white'
                  : 'bg-[var(--hover-bg-subtle)] text-[var(--text-muted)] border border-[var(--border-subtle)] hover:text-[var(--text-secondary)]'
              }`}
              data-testid={`numpad-side-${key}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Auto-Validate — background contradiction detection */}
      <div className="mt-3">
        <label className="text-xs text-[var(--text-muted)] mb-1.5 block">
          Auto-Validate <span className="text-[var(--text-disabled)] font-normal">(silent check as you play)</span>
        </label>
        <div className="flex gap-1.5">
          {[
            [false, 'Off'],
            [true, 'On'],
          ].map(([key, label]) => (
            <button
              key={String(key)}
              onClick={() => updatePref('autoValidate', key)}
              className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                !!prefs.autoValidate === key
                  ? 'bg-[var(--accent-primary)] text-white'
                  : 'bg-[var(--hover-bg-subtle)] text-[var(--text-muted)] border border-[var(--border-subtle)] hover:text-[var(--text-secondary)]'
              }`}
              data-testid={`auto-validate-${label.toLowerCase()}`}
            >
              {label}
            </button>
          ))}
        </div>
        <p className="text-[10px] text-[var(--text-disabled)] mt-1 leading-snug">
          When ON, the Validate button lights amber the moment a contradiction appears.
        </p>
      </div>

      {/* Sound Effects — celebratory piano riff on puzzle completion */}
      <div className="mt-3">
        <label className="text-xs text-[var(--text-muted)] mb-1.5 block">
          Sound Effects <span className="text-[var(--text-disabled)] font-normal">(victory piano)</span>
        </label>
        <div className="flex gap-1.5">
          {[
            [false, 'Off'],
            [true, 'On'],
          ].map(([key, label]) => (
            <button
              key={String(key)}
              onClick={() => updatePref('soundEffects', key)}
              className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                (prefs.soundEffects !== false) === key
                  ? 'bg-[var(--accent-primary)] text-white'
                  : 'bg-[var(--hover-bg-subtle)] text-[var(--text-muted)] border border-[var(--border-subtle)] hover:text-[var(--text-secondary)]'
              }`}
              data-testid={`sound-effects-${label.toLowerCase()}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
    </Card>
  );
});

// Walkthrough Panel Component
//
// Map of technique name → tutorial id (corresponds to DIFFICULTY_LEVELS in
// the backend). Used to render a "Teach me" link next to each step's
// technique pill — clicking opens the matching tutorial in a new tab so
// the user can learn the technique without losing their puzzle state.
const TECHNIQUE_TUTORIAL_ID = {
  'Naked Single': 1,
  'Hidden Single': 2,
  'Naked Pair': 3,
  'Hidden Pair': 4,
  'Naked Triple': 5,
  'Hidden Triple': 6,
  'X-Wing': 7,
  'Pointing Pair': 8,
  'Box-Line Reduction': 9,
  'Naked Quad': 10,
  'Hidden Quad': 11,
  'Swordfish': 12,
  'XY-Wing': 13,
  'Simple Coloring': 14,
  'AIC': 15,
  'XYZ-Wing': 15,
  'ALS-XZ': 16,
  'Forcing Chain': 17,
};

const WalkthroughPanel = React.memo(function WalkthroughPanel({ 
  steps, 
  currentIndex, 
  onPrev, 
  onNext, 
  onExit, 
  autoPlaying, 
  onToggleAutoPlay,
  userTier,
  difficultyLevel,
  onUpgrade,
}) {
  const step = steps[currentIndex];
  // After my walkthrough preview-then-commit change, indices 0..N-1 are
  // "preview" indices and index N is "solve complete, all placements
  // committed". `isLast` here means "no more steps to step through".
  const isLast = currentIndex >= steps.length;
  const total = steps.length;
  const progressPct = Math.min(100, ((currentIndex) / total) * 100);
  const stepLabel = isLast ? `Solve complete · ${total}/${total}` : `Step ${currentIndex + 1} / ${total}`;

  const techniquesUsed = useMemo(() => {
    const counts = {};
    steps.forEach(s => {
      counts[s.technique] = (counts[s.technique] || 0) + 1;
    });
    return counts;
  }, [steps]);

  return (
    <Card className="bg-[var(--background-surface)] border-[var(--accent-primary)]/30 p-3 space-y-3" data-testid="walkthrough-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-[var(--accent-primary)]" />
          <span className="text-sm font-semibold text-[var(--text-primary)]">Step-by-Step Solve</span>
        </div>
        <button onClick={onExit} className="p-1 rounded hover:bg-[var(--hover-bg)] text-[var(--text-muted)]" data-testid="exit-walkthrough-btn">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="flex items-center justify-between text-xs text-[var(--text-muted)]">
        <span>{stepLabel}</span>
        <div className="flex-1 mx-3 h-1 rounded-full bg-[var(--hover-bg-subtle)] overflow-hidden">
          <div 
            className="h-full bg-[var(--accent-primary)] transition-all duration-300 rounded-full"
            style={{ width: `${progressPct}%` }}
          />
        </div>
        <span>{Math.round(progressPct)}%</span>
      </div>

      {step && (
        <div className="rounded-lg bg-[var(--hover-bg-subtle)] p-3 space-y-1.5">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-[var(--accent-primary)]/15 text-[var(--accent-primary)]">
              {step.technique}
            </span>
            <span className="text-xs text-[var(--text-muted)]">
              {step.type === 'placement' ? 'Value Placement' : 'Candidate Elimination'}
            </span>
            {TECHNIQUE_TUTORIAL_ID[step.technique] && (
              <a
                href={`/tutorials/${TECHNIQUE_TUTORIAL_ID[step.technique]}`}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-auto text-[10px] font-medium text-[var(--accent-primary)] hover:underline inline-flex items-center gap-1"
                data-testid={`teach-me-${step.technique.toLowerCase().replace(/\s+/g, '-')}`}
                title={`Open the ${step.technique} tutorial in a new tab`}
              >
                <BookOpen className="w-3 h-3" />
                Teach me
              </a>
            )}
          </div>
          {step.type === 'placement' ? (
            <p className="text-sm font-medium text-[var(--text-primary)]">
              Place <span className="text-[var(--accent-primary)] font-bold text-base">{step.value}</span> at Row {step.row + 1}, Column {step.col + 1}
            </p>
          ) : (
            <p className="text-sm font-medium text-[var(--text-primary)]">
              Eliminate candidates from {step.cells?.length || 0} cell{(step.cells?.length || 0) !== 1 ? 's' : ''}
            </p>
          )}
          <p className="text-xs text-[var(--text-muted)] leading-relaxed italic">
            {step.explanation}
          </p>
        </div>
      )}

      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={onPrev}
          disabled={currentIndex <= 0}
          className="flex-1 border-[var(--border-subtle)] text-xs"
          data-testid="walkthrough-prev"
        >
          <ChevronLeft className="w-3.5 h-3.5 mr-1" />
          Prev
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onToggleAutoPlay}
          className={`px-3 border-[var(--border-subtle)] text-xs ${autoPlaying ? 'bg-[var(--accent-primary)]/10 border-[var(--accent-primary)]/40 text-[var(--accent-primary)]' : ''}`}
          data-testid="walkthrough-autoplay"
        >
          {autoPlaying ? <Square className="w-3 h-3" /> : <SkipForward className="w-3.5 h-3.5" />}
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onNext}
          disabled={isLast}
          className="flex-1 border-[var(--border-subtle)] text-xs"
          data-testid="walkthrough-next"
        >
          Next
          <ChevronRight className="w-3.5 h-3.5 ml-1" />
        </Button>
      </div>

      {isLast && (
        <>
          <div className="rounded-lg bg-[var(--accent-primary)]/5 border border-[var(--accent-primary)]/20 p-2.5 space-y-1">
            <p className="text-xs font-semibold text-[var(--accent-primary)]">Solve Complete!</p>
            <div className="flex flex-wrap gap-1">
              {Object.entries(techniquesUsed).map(([tech, count]) => (
                <span key={tech} className="text-[10px] px-1.5 py-0.5 rounded bg-[var(--hover-bg-subtle)] text-[var(--text-muted)]">
                  {tech}: {count}x
                </span>
              ))}
            </div>
          </div>

          {!IS_ANDROID_APP && userTier === 'free' && difficultyLevel <= 3 && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 space-y-2" data-testid="premium-upsell">
              <p className="text-sm font-semibold text-[var(--text-primary)]">
                Ready for the next challenge?
              </p>
              <p className="text-xs text-[var(--text-muted)] leading-relaxed">
                You&apos;ve mastered the basics! Premium unlocks <strong>13 advanced techniques</strong> like X-Wing, Swordfish, XY-Wing, and more — each with step-by-step walkthroughs to guide you.
              </p>
              <button
                onClick={onUpgrade}
                className="w-full py-2 rounded-lg text-sm font-semibold bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:from-amber-600 hover:to-orange-600 transition-all"
                data-testid="premium-upsell-btn"
              >
                Unlock All 17 Levels — $4.99/mo
              </button>
            </div>
          )}
        </>
      )}
    </Card>
  );
});

// Map raw source_label strings to short human-readable badge text. Sources
// can be things like "nyt-medium-img1497", "NYT 2024-12-04 Hard", or
// "auto-deflated from L9 (parent=…)" — render them all as a tight chip.
function curatedBadgeText(label) {
  if (!label) return 'Curated';
  const s = String(label).trim();
  if (/^auto-deflated/i.test(s)) {
    const m = s.match(/L(\d+)/i);
    return m ? `Curated · deflated from L${m[1]}` : 'Curated · deflated';
  }
  if (/nyt|new\s*york\s*times/i.test(s)) return 'NYT · curated';
  if (s.length > 22) return 'Curated';
  return `Curated · ${s}`;
}

// Main GamePage Component
export default function GamePage() {
  const { puzzleId } = useParams();
  const navigate = useNavigate();
  const { user, refreshAuth } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { prefs } = useGamePreferences();
  
  const [loading, setLoading] = useState(true);
  const [sessionId, setSessionId] = useState(null);
  const [difficulty, setDifficulty] = useState({ level: 1, name: 'Loading...' });
  // Curriculum metadata for banked levels (Phase 3). Null for non-banked.
  const [series, setSeries] = useState({ index: null, total: null });
  // Provenance — admin_uploaded + source_label drive the "Curated" badge so
  // users get social proof when a puzzle came from us (NYT transcription,
  // hand-vetted set, or auto-deflated parent) instead of pure generation.
  const [curated, setCurated] = useState({ admin: false, label: null });
  // Mobile-only: collapse the secondary toolbar (Focus/Draw/Validate/Solve/
  // Display/etc.) behind a 3-dot toggle so the grid + numpad fit 100% on
  // small screens by default. Desktop ignores this state via CSS.
  const [mobileToolsOpen, setMobileToolsOpen] = useState(false);
  const [noteMode, setNoteMode] = useState(false);
  const [gameComplete, setGameComplete] = useState(false);
  const [unlockedAchievements, setUnlockedAchievements] = useState(null);
  const { fact: wisdomFact, fading: wisdomFading } = useWisdomFact();

  const {
    grid,
    originalGrid,
    pencilMarks,
    errors,
    wrongCells,
    setWrongCells,
    clearWrongCells,
    initializeGrid,
    setCellValue,
    togglePencilMark,
    autoFillCandidates,
    resetGame,
    undo,
    redo,
    canUndo,
    canRedo,
  } = useSudokuGame();

  const {
    timeElapsed,
    isPaused,
    pause,
    resume,
    toggle: togglePause,
    reset: resetTimer,
    stop: stopTimer,
    formatTime
  } = useGameTimer(!loading && !gameComplete);

  const {
    selectedCell,
    selectCell,
    moveSelection,
    isSelected,
    isRelated,
    isSameNumber,
    isUnrelated,
    focusMode,
    focusIntensity,
    setFocusIntensity,
    toggleFocusMode,
  } = useCellSelection(grid);

  const [scribbleActive, setScribbleActive] = useState(false);
  const [scribbleEraserActive, setScribbleEraserActive] = useState(false);
  const [scribbleColor, setScribbleColor] = useState('#0094FF'); // default cyan-blue
  const scribbleClearRef = useRef(null);
  const scribbleRef = useRef(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Walkthrough state
  const [walkthroughActive, setWalkthroughActive] = useState(false);
  const [walkthroughSteps, setWalkthroughSteps] = useState([]);
  const [walkthroughIndex, setWalkthroughIndex] = useState(0);
  const [walkthroughLoading, setWalkthroughLoading] = useState(false);
  const [autoPlaying, setAutoPlaying] = useState(false);
  const autoPlayRef = useRef(null);

  // Compute derived values from preferences
  const highlightRGB = HIGHLIGHT_PRESETS[prefs.highlightColor]?.rgb || '0, 122, 255';
  const answerPreset = ANSWER_PRESETS[prefs.answerStyle];
  const answerColorValue = prefs.answerStyle === 'adaptive' || prefs.answerStyle === 'dark'
    ? null
    : (theme === 'dark' ? answerPreset?.dark : answerPreset?.light) || answerPreset?.dark;

  const toggleScribble = useCallback(() => {
    setScribbleActive(prev => {
      const next = !prev;
      if (!next) setScribbleEraserActive(false); // turn off eraser when leaving draw mode
      return next;
    });
  }, []);

  const toggleScribbleEraser = useCallback(() => {
    if (!scribbleActive) return;
    setScribbleEraserActive(prev => !prev);
  }, [scribbleActive]);

  const clearScribble = useCallback(() => {
    if (scribbleClearRef.current) scribbleClearRef.current();
  }, []);

  const timeDisplay = useMemo(() => formatTime(), [formatTime]);

  // ── Walkthrough logic ──
  const startWalkthrough = useCallback(async () => {
    // If the user has placed any wrong values, halt and show them first.
    if (errors && errors.length > 0) {
      const first = errors[0];
      toast.error(
        `Please fix wrong entries before solving. ${errors.length} error${errors.length > 1 ? 's' : ''} shown in red — start with Row ${first[0] + 1}, Col ${first[1] + 1}.`,
        { duration: 5000 }
      );
      // Select the first error cell so the user sees it
      selectCell(first[0], first[1]);
      return;
    }
    setWalkthroughLoading(true);
    try {
      // Include the user's pencil marks so the walkthrough starts from
      // their current mental state, not a from-scratch candidate recompute.
      const res = await axios.post(
        `${API_URL}/api/puzzles/${puzzleId}/walkthrough`,
        { grid, pencil_marks: pencilMarks },
        { withCredentials: true }
      );
      if (res.data.steps.length === 0) {
        toast.info('Puzzle is already complete!');
        return;
      }
      setWalkthroughSteps(res.data.steps);
      setWalkthroughIndex(0);
      setWalkthroughActive(true);
      setAutoPlaying(false);
      pause();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load walkthrough');
    } finally {
      setWalkthroughLoading(false);
    }
  }, [puzzleId, grid, pencilMarks, pause, errors, selectCell]);

  const stopWalkthrough = useCallback(() => {
    setWalkthroughActive(false);
    setWalkthroughSteps([]);
    setWalkthroughIndex(0);
    setAutoPlaying(false);
    if (autoPlayRef.current) clearInterval(autoPlayRef.current);
  }, []);

  // ── Validate Grid ──
  // Pre-emptive consistency check. Unlike error-cells (which compare against
  // the stored solution), the validator reasons from the grid state alone —
  // so it works even when the user is ahead of our error detection.
  const [validating, setValidating] = useState(false);
  const [validationIssue, setValidationIssue] = useState(false); // set by auto-validate

  // "Stuck?" flow: validator + check-work + solver nudge, then a community
  // help thread whose replies are piped back next to the grid.
  const [stuckOpen, setStuckOpen] = useState(false);
  const [helpReq, setHelpReq] = useState(null);
  const [threadOpen, setThreadOpen] = useState(false);
  const loadHelp = useCallback(() => {
    if (!puzzleId || !user) return;
    axios.get(`${API_URL}/api/help-requests/by-puzzle/${puzzleId}`, { withCredentials: true })
      .then((res) => setHelpReq(res.data?.request || null))
      .catch(() => {});
  }, [puzzleId, user]);
  useEffect(() => { setHelpReq(null); setThreadOpen(false); loadHelp(); }, [loadHelp]);
  useEffect(() => {
    if (!helpReq || helpReq.status !== 'open') return undefined;
    const t = setInterval(loadHelp, threadOpen ? 30000 : 60000);
    return () => clearInterval(t);
  }, [helpReq?.id, helpReq?.status, threadOpen, loadHelp]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!threadOpen || !helpReq?.unread) return;
    axios.post(`${API_URL}/api/help-requests/${helpReq.id}/seen`, {}, { withCredentials: true })
      .then(() => { setHelpReq((h) => (h ? { ...h, unread: 0 } : h)); window.dispatchEvent(new Event('help-replies-seen')); })
      .catch(() => {});
  }, [threadOpen, helpReq?.unread, helpReq?.id]);
  const handleStuck = useCallback(() => {
    if (!user) {
      toast.info('Sign in to get help with this puzzle');
      return;
    }
    setStuckOpen(true);
  }, [user]);
  const handleValidate = useCallback(async (silent = false) => {
    if (!puzzleId) return;
    if (!silent) setValidating(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/puzzles/${puzzleId}/validate`,
        grid,
        { withCredentials: true }
      );
      const r = res.data;
      if (r.valid) {
        setValidationIssue(false);
        if (!silent) {
          toast.success('Grid is consistent — no logical contradictions.', { duration: 4000 });
        }
        return;
      }
      setValidationIssue(true);
      if (silent) return; // auto-validate never surfaces a toast; just lights the button
      const strong = (r.suspects || []).filter(s => s.strong && !s.is_original_clue);
      const first = strong[0] || (r.suspects || []).find(s => !s.is_original_clue);
      if (first) {
        selectCell(first.row, first.col);
        const note = strong.length > 0
          ? `Likely wrong entry: R${first.row + 1}C${first.col + 1} = ${first.value}. Removing it restores the puzzle.`
          : `${r.reason} Most likely culprit: R${first.row + 1}C${first.col + 1} = ${first.value}.`;
        toast.error(note, { duration: 8000 });
      } else {
        toast.error(r.reason || 'Grid has a contradiction.', { duration: 6000 });
      }
    } catch (err) {
      if (!silent) toast.error(err.response?.data?.detail || 'Validator failed.');
    } finally {
      if (!silent) setValidating(false);
    }
  }, [puzzleId, grid, selectCell]);

  // "Check My Work" — on-demand solution comparison. Highlights any
  // user-placed cell that doesn't match the puzzle's stored solution,
  // without revealing what the correct digit is. Pairs with
  // `clearWrongCells` from the hook, which offers a one-click undo of
  // every flagged wrong entry.
  const [checkingWork, setCheckingWork] = useState(false);
  const handleCheckWork = useCallback(async () => {
    if (!puzzleId) return;
    setCheckingWork(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/puzzles/${puzzleId}/check-work`,
        { grid },
        { withCredentials: true }
      );
      const wrong = res.data?.wrong_cells || [];
      const asTuples = wrong.map((w) => [w.row, w.col]);
      setWrongCells(asTuples);
      if (asTuples.length === 0) {
        toast.success('Every entry matches the solution. Keep going!', { duration: 4000 });
      } else {
        const first = wrong[0];
        selectCell(first.row, first.col);
        toast.error(
          `${asTuples.length} incorrect ${asTuples.length === 1 ? 'entry' : 'entries'} highlighted in red. Use "Clear Wrong" to reverse them all.`,
          { duration: 8000 }
        );
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        toast.error('Sign in to check your work.');
      } else {
        toast.error(err?.response?.data?.detail || 'Check-work failed.');
      }
    } finally {
      setCheckingWork(false);
    }
  }, [puzzleId, grid, selectCell, setWrongCells]);

  // Merge the ephemeral wrong-cells list into the errors array that the
  // board renders, so flagged cells inherit the existing red styling
  // without duplicating CSS classes. De-duped in case a cell is both
  // internally-invalid AND wrong-vs-solution.
  const displayErrors = useMemo(() => {
    if (!wrongCells || wrongCells.length === 0) return errors;
    const seen = new Set(errors.map(([r, c]) => `${r},${c}`));
    const merged = [...errors];
    for (const [r, c] of wrongCells) {
      const key = `${r},${c}`;
      if (!seen.has(key)) {
        merged.push([r, c]);
        seen.add(key);
      }
    }
    return merged;
  }, [errors, wrongCells]);

  // Auto-validate: if user enabled it, silently re-check after every grid
  // change. Debounced to avoid hammering the backend during rapid entry.
  useEffect(() => {
    if (!prefs.autoValidate || !puzzleId || gameComplete || walkthroughActive) {
      setValidationIssue(false);
      return;
    }
    const t = setTimeout(() => {
      handleValidate(true);
    }, 600);
    return () => clearTimeout(t);
  }, [prefs.autoValidate, grid, puzzleId, gameComplete, walkthroughActive, handleValidate]);

  const walkthroughNext = useCallback(() => {
    // Allow advancing one past the last step so the FINAL step's placement
    // gets committed visually. Indices 0..N-1 are "preview" indices; index
    // N is "all placements committed, no current preview."
    setWalkthroughIndex(prev => Math.min(prev + 1, walkthroughSteps.length));
  }, [walkthroughSteps.length]);

  const walkthroughPrev = useCallback(() => {
    setWalkthroughIndex(prev => Math.max(prev - 1, 0));
  }, []);

  const toggleAutoPlay = useCallback(() => {
    setAutoPlaying(prev => !prev);
  }, []);

  // Auto-play interval
  useEffect(() => {
    if (autoPlaying && walkthroughActive) {
      autoPlayRef.current = setInterval(() => {
        setWalkthroughIndex(prev => {
          // Allow advancing one past last so the final step's placement
          // commits visually before we stop autoplay.
          if (prev >= walkthroughSteps.length) {
            setAutoPlaying(false);
            return prev;
          }
          return prev + 1;
        });
      }, 1500);
    } else if (autoPlayRef.current) {
      clearInterval(autoPlayRef.current);
    }
    return () => { if (autoPlayRef.current) clearInterval(autoPlayRef.current); };
  }, [autoPlaying, walkthroughActive, walkthroughSteps.length]);

  // Build the walkthrough grid (current state + placements applied STRICTLY
  // BEFORE the current index). The current step is "previewed" — its cell
  // is highlighted and the candidate is circled, but the value isn't filled
  // until the user clicks Next, advancing the cursor past it. This matches
  // the user's stated mental model: "show me the logic first, then commit."
  const walkthroughGrid = useMemo(() => {
    if (!walkthroughActive || !walkthroughSteps.length) return null;
    const wGrid = grid.map(r => [...r]);
    for (let i = 0; i < walkthroughIndex; i++) {
      const step = walkthroughSteps[i];
      if (step.type === 'placement') {
        wGrid[step.row][step.col] = step.value;
      }
    }
    return wGrid;
  }, [walkthroughActive, walkthroughSteps, walkthroughIndex]);

  // Build the walkthrough pencil marks in lockstep with walkthroughGrid.
  // Every time the solver places a digit, that digit MUST also be
  // eliminated as a candidate from the row/col/box it sits in — otherwise
  // stale candidates linger and make it impossible to follow the logic of
  // subsequent steps (the exact bug the user reported). We also apply any
  // explicit candidate-elimination steps the backend emitted.
  const walkthroughPencilMarks = useMemo(() => {
    if (!walkthroughActive || !walkthroughSteps.length) return pencilMarks;
    let marks = pencilMarks.map(row => row.map(cell => [...cell]));
    for (let i = 0; i < walkthroughIndex; i++) {
      const step = walkthroughSteps[i];
      if (step.type === 'placement') {
        marks = removeCandidateFromRelated(marks, step.row, step.col, step.value);
      } else if (step.type === 'elimination' && Array.isArray(step.cells)) {
        // Backend elimination steps carry the exact (row,col,value) triples
        // to strike; apply them faithfully so the visible candidate grid
        // matches the solver's reasoning.
        for (const cell of step.cells) {
          if (cell && typeof cell.row === 'number' && typeof cell.col === 'number' && typeof cell.value === 'number') {
            marks[cell.row][cell.col] = marks[cell.row][cell.col].filter(v => v !== cell.value);
          }
        }
      }
    }
    return marks;
  }, [walkthroughActive, walkthroughSteps, walkthroughIndex, pencilMarks]);

  // Walkthrough highlight cell — primary cell to draw attention to.
  // Backend now sets row/col on elim steps too, but for richer multi-cell highlight
  // we also expose `cells` (all cells affected by an elimination step).
  const walkthroughHighlightCell = walkthroughActive && walkthroughSteps[walkthroughIndex]
    ? { row: walkthroughSteps[walkthroughIndex].row, col: walkthroughSteps[walkthroughIndex].col }
    : null;

  const walkthroughElimCells = useMemo(() => {
    if (!walkthroughActive) return [];
    const step = walkthroughSteps[walkthroughIndex];
    if (!step || step.type !== 'elimination' || !step.cells) return [];
    return step.cells;
  }, [walkthroughActive, walkthroughSteps, walkthroughIndex]);

  // Reference cells for placement steps — the "witness" cells whose content
  // (or exclusions) force the current placement. For Naked Single these are
  // the filled peers that leave only one digit. For Hidden Single these are
  // the 8 other cells in the forcing unit. The frontend highlights these
  // cells in a secondary shade so the user can visually trace the logic.
  const walkthroughReferenceCells = useMemo(() => {
    if (!walkthroughActive) return [];
    const step = walkthroughSteps[walkthroughIndex];
    if (!step || !Array.isArray(step.references)) return [];
    return step.references;
  }, [walkthroughActive, walkthroughSteps, walkthroughIndex]);

  // Pending placement preview — the value the current step is *about to*
  // place. Shown as a circled candidate in the highlighted cell so the
  // user sees WHAT will happen and WHY before committing. Click Next →
  // walkthroughGrid catches up and fills the cell.
  const walkthroughPendingPlacement = useMemo(() => {
    if (!walkthroughActive) return null;
    const step = walkthroughSteps[walkthroughIndex];
    if (!step || step.type !== 'placement') return null;
    return { row: step.row, col: step.col, value: step.value };
  }, [walkthroughActive, walkthroughSteps, walkthroughIndex]);

  // Walkthrough selection overrides
  const isWalkthroughSelected = useCallback((row, col) => {
    if (!walkthroughHighlightCell) return false;
    if (row === walkthroughHighlightCell.row && col === walkthroughHighlightCell.col) return true;
    // For elimination steps, treat every affected cell as "selected" so the user sees them all.
    if (walkthroughElimCells.some(ec => ec.row === row && ec.col === col)) return true;
    return false;
  }, [walkthroughHighlightCell, walkthroughElimCells]);

  // Highlight reference cells (peers that force the placement) with a
  // softer "related" state — user can trace the deduction visually.
  const isWalkthroughReferenced = useCallback((row, col) => {
    if (!walkthroughReferenceCells.length) return false;
    return walkthroughReferenceCells.some(
      (rc) => rc.row === row && rc.col === col,
    );
  }, [walkthroughReferenceCells]);

  const isWalkthroughRelated = useCallback((row, col) => {
    // Reference cells take priority over generic "peer of target" shading —
    // they're the ones the logic actually cites.
    if (isWalkthroughReferenced(row, col)) return true;
    if (!walkthroughHighlightCell) return false;
    const { row: hr, col: hc } = walkthroughHighlightCell;
    if (row === hr && col === hc) return false;
    if (walkthroughElimCells.some(ec => ec.row === row && ec.col === col)) return false;
    return row === hr || col === hc ||
      (Math.floor(row / 3) === Math.floor(hr / 3) && Math.floor(col / 3) === Math.floor(hc / 3));
  }, [walkthroughHighlightCell, walkthroughElimCells, isWalkthroughReferenced]);

  // Effect 1: Load puzzle on mount
  useEffect(() => {
    let mounted = true;
    
    const loadPuzzle = async () => {
      try {
        const puzzleRes = await axios.get(`${API_URL}/api/puzzles/${puzzleId}`, { withCredentials: true });
        if (!mounted) return;
        
        const puzzle = puzzleRes.data;
        initializeGrid(puzzle.grid);
        setDifficulty({ level: puzzle.difficulty_level, name: puzzle.difficulty_name });
        setSeries({
          index: puzzle.series_index ?? null,
          total: puzzle.series_total ?? null,
        });
        setCurated({
          admin: Boolean(puzzle.admin_uploaded),
          label: puzzle.source_label ?? null,
        });
        
        const sessionRes = await axios.post(
          `${API_URL}/api/sessions/start`,
          { puzzle_id: puzzleId },
          { withCredentials: true }
        );
        if (!mounted) return;
        
        setSessionId(sessionRes.data.id);
        setLoading(false);
      } catch (err) {
        if (!mounted) return;
        toast.error('Failed to load puzzle');
        navigate('/play');
      }
    };

    loadPuzzle();
    return () => { mounted = false; };
  }, [puzzleId, navigate, initializeGrid]);

  // Effect 2: Keyboard handling (separate effect)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (gameComplete || isPaused || !selectedCell) return;
      
      const [row, col] = selectedCell;
      
      if (e.key >= '1' && e.key <= '9') {
        const num = parseInt(e.key, 10);
        if (noteMode) {
          togglePencilMark(row, col, num);
        } else {
          setCellValue(row, col, num);
        }
      } else if (e.key === 'Backspace' || e.key === 'Delete' || e.key === '0') {
        setCellValue(row, col, 0);
      } else if (e.key === ' ') {
        setNoteMode(prev => !prev);
      } else if (e.key === 'ArrowUp') {
        moveSelection('up');
      } else if (e.key === 'ArrowDown') {
        moveSelection('down');
      } else if (e.key === 'ArrowLeft') {
        moveSelection('left');
      } else if (e.key === 'ArrowRight') {
        moveSelection('right');
      } else if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        if (scribbleActive && scribbleRef.current?.canUndo()) {
          scribbleRef.current.undo();
        } else {
          undo();
        }
      } else if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.shiftKey && e.key === 'z'))) {
        e.preventDefault();
        if (scribbleActive && scribbleRef.current?.canRedo()) {
          scribbleRef.current.redo();
        } else {
          redo();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedCell, noteMode, gameComplete, isPaused, setCellValue, togglePencilMark, moveSelection, undo, redo, scribbleActive]);

  // Effect 3: Check completion (separate effect)
  useEffect(() => {
    if (gameComplete || !sessionId) return;
    
    const allFilled = grid.every(row => row.every(cell => cell !== 0));
    if (!allFilled || errors.length > 0) return;

    let mounted = true;

    const verifyCompletion = async () => {
      try {
        const res = await axios.post(
          `${API_URL}/api/puzzles/${puzzleId}/check`,
          grid,
          { withCredentials: true }
        );

        if (!mounted) return;

        if (res.data.correct) {
          setGameComplete(true);
          stopTimer();

          // Celebratory piano flourish — honors the user's
          // soundEffects preference (defaults ON). Wrapped in try
          // so an AudioContext failure can never break completion.
          if (prefs.soundEffects !== false) {
            try { playVictoryRiff(); } catch { /* AudioContext blocked */ }
          }

          const { data: completion } = await axios.post(
            `${API_URL}/api/sessions/${sessionId}/complete`,
            grid,
            { 
              params: { time_elapsed: timeElapsed },
              withCredentials: true 
            }
          );

          toast.success('Congratulations! Puzzle completed!');

          // Refresh user state so the streak indicator updates immediately.
          refreshAuth?.();

          // Newly unlocked achievements: queue them for the UnlockModal.
          const newly = completion?.new_achievements || [];
          if (newly.length > 0) {
            // Delay modal so the "completed" toast is visible first.
            setTimeout(() => setUnlockedAchievements(newly), 700);
          }
        }
      } catch (err) {
        toast.error('Failed to verify solution');
      }
    };

    verifyCompletion();
    return () => { mounted = false; };
  }, [grid, errors, gameComplete, puzzleId, sessionId, timeElapsed, stopTimer]);

  // Abandon beacon: fire /sessions/{id}/abandon when the user leaves the
  // game before completing it. Wired to both `pagehide` (covers tab
  // close, back/forward navigation, mobile app-switch) and React Router
  // unmount. Idempotent on the server side — safe to fire multiple times.
  // sendBeacon survives even when the tab is being torn down, unlike a
  // normal fetch which would be cancelled.
  useEffect(() => {
    if (!sessionId) return;
    let fired = false;
    const fireBeacon = () => {
      if (fired) return;
      if (gameComplete) return;
      fired = true;
      try {
        const url = `${API_URL}/api/sessions/${sessionId}/abandon`;
        if (navigator.sendBeacon) {
          // sendBeacon needs an actual Blob/FormData body
          navigator.sendBeacon(url, new Blob([], { type: 'application/json' }));
        } else {
          // Fallback for ancient browsers
          fetch(url, { method: 'POST', credentials: 'include', keepalive: true }).catch(() => { /* ignore */ });
        }
      } catch { /* beacon errors are non-fatal */ }
    };
    window.addEventListener('pagehide', fireBeacon);
    return () => {
      window.removeEventListener('pagehide', fireBeacon);
      // Component unmounting (user navigated to another route) — also abandon
      fireBeacon();
    };
  }, [sessionId, gameComplete]);

  // Callbacks
  const handleNumberClick = useCallback((num) => {
    if (!selectedCell) return;
    // Prime the AudioContext on first user input so the victory
    // riff plays without lag on iOS Safari (autoplay policy).
    primeAudio();
    const [row, col] = selectedCell;
    if (noteMode) {
      togglePencilMark(row, col, num);
    } else {
      setCellValue(row, col, num);
    }
  }, [selectedCell, noteMode, setCellValue, togglePencilMark]);

  const handleErase = useCallback(() => {
    if (!selectedCell) return;
    const [row, col] = selectedCell;
    setCellValue(row, col, 0);
  }, [selectedCell, setCellValue]);

  const handleHint = useCallback(async () => {
    // Block hints while wrong entries are on the board — they'd be misleading
    if (errors && errors.length > 0) {
      const first = errors[0];
      toast.error(
        `Wrong entry detected at Row ${first[0] + 1}, Col ${first[1] + 1} (shown in red). Fix errors before requesting a hint.`,
        { duration: 5000 }
      );
      selectCell(first[0], first[1]);
      return;
    }
    try {
      // Send both grid and the user's current pencil marks. The backend
      // solver intersects computed candidates with the user's marks so
      // hints stop suggesting eliminations the user has already made.
      const res = await axios.post(
        `${API_URL}/api/puzzles/${puzzleId}/hint${sessionId ? `?session_id=${sessionId}` : ''}`,
        { grid, pencil_marks: pencilMarks },
        { withCredentials: true }
      );

      const hint = res.data;
      if (hint.type === 'complete') {
        toast.success('Puzzle is already complete!');
        return;
      }

      if (hint.row !== undefined) {
        selectCell(hint.row, hint.col);
      }

      if (hint.type === 'placement') {
        toast.info(
          `${hint.technique}: Place ${hint.value} at Row ${hint.row + 1}, Col ${hint.col + 1}`,
          { description: hint.explanation, duration: 8000 }
        );
      } else if (hint.type === 'elimination') {
        toast.info(
          `${hint.technique}: Eliminate ${hint.eliminated.join(', ')} from Row ${hint.row + 1}, Col ${hint.col + 1}`,
          { description: hint.explanation, duration: 8000 }
        );
      } else if (hint.type === 'stuck') {
        // Honest "I cannot derive a next step" — most often a wrong entry
        // upstream. Surface as a warning toast with the explanation and
        // nudge toward Validate Grid.
        toast.warning(hint.technique || 'No logical step found', {
          description: hint.explanation,
          duration: 10000,
        });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to get hint');
    }
  }, [puzzleId, grid, pencilMarks, selectCell, errors, sessionId]);

  const handleReset = useCallback(() => {
    resetGame();
    resetTimer();
    setGameComplete(false);
  }, [resetGame, resetTimer]);

  const [resetDialogOpen, setResetDialogOpen] = useState(false);

  const handleResetPuzzle = useCallback(() => {
    handleReset();
    if (scribbleClearRef.current) scribbleClearRef.current();
    setResetDialogOpen(false);
  }, [handleReset]);

  const handleResetScribbleOnly = useCallback(() => {
    if (scribbleClearRef.current) scribbleClearRef.current();
    setResetDialogOpen(false);
  }, []);

  const handleShare = useCallback(() => {
    const text = `🧩 I solved a ${difficulty.name} puzzle (Level ${difficulty.level}) in ${timeDisplay}!\n\nCan you beat my time? 🎯\n\n#SudokuAcademy #Sudoku #BrainGames`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  }, [difficulty.name, difficulty.level, timeDisplay]);

  const handleBack = useCallback(() => {
    navigate('/play');
  }, [navigate]);

  const handleToggleNotes = useCallback(() => {
    setNoteMode(prev => !prev);
  }, []);

  const handleUndo = useCallback(() => {
    if (scribbleActive && scribbleRef.current?.canUndo()) {
      scribbleRef.current.undo();
    } else {
      undo();
    }
  }, [undo, scribbleActive]);

  const handleRedo = useCallback(() => {
    if (scribbleActive && scribbleRef.current?.canRedo()) {
      scribbleRef.current.redo();
    } else {
      redo();
    }
  }, [redo, scribbleActive]);

  // Grid zoom + pan: lets the user resize AND drag the grid without
  // fighting the browser. Persists per-browser so preferences survive
  // reloads. Two-finger pinch resizes; two-finger drag pans vertically.
  // Both work simultaneously so you can pan while pinching.
  const [gridScale, setGridScale] = useState(() => {
    const saved = typeof window !== 'undefined' ? window.localStorage.getItem('gridScale') : null;
    const n = saved ? parseFloat(saved) : 1;
    return Number.isFinite(n) && n >= 0.6 && n <= 1.8 ? n : 1;
  });
  const [gridPanY, setGridPanY] = useState(() => {
    const saved = typeof window !== 'undefined' ? window.localStorage.getItem('gridPanY') : null;
    const n = saved ? parseFloat(saved) : 0;
    return Number.isFinite(n) ? n : 0;
  });
  const gridAreaRef = useRef(null);
  // Track the starting distance, centroid Y, scale AND pan when a
  // 2-finger gesture begins. We keep pinch and pan on the SAME gesture
  // so a user can zoom and reposition in one continuous motion.
  const pinchStateRef = useRef({
    active: false,
    startDist: 0,
    startCentroidY: 0,
    startScale: 1,
    startPanY: 0,
  });

  useEffect(() => {
    try { window.localStorage.setItem('gridScale', String(gridScale)); } catch (_) { /* localStorage blocked */ }
  }, [gridScale]);
  useEffect(() => {
    try { window.localStorage.setItem('gridPanY', String(gridPanY)); } catch (_) { /* localStorage blocked */ }
  }, [gridPanY]);

  const clampScale = (v) => Math.max(0.6, Math.min(1.8, v));
  // Clamp pan to ±40% of viewport height so the grid never disappears
  // off-screen. Recomputed on every gesture tick against the LIVE viewport
  // so device rotation / URL-bar collapse never leaves the grid stranded.
  const clampPan = (v) => {
    const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
    const max = Math.round(vh * 0.4);
    return Math.max(-max, Math.min(max, v));
  };
  const zoomIn = useCallback(() => setGridScale(s => clampScale(+(s + 0.1).toFixed(2))), []);
  const zoomOut = useCallback(() => setGridScale(s => clampScale(+(s - 0.1).toFixed(2))), []);

  // Wheel (scroll) zoom on grid — no Ctrl required. Passive:false so we can preventDefault.
  useEffect(() => {
    const el = gridAreaRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.06 : 0.06;
      setGridScale(s => clampScale(+(s + delta).toFixed(3)));
    };
    const onTouchStart = (e) => {
      if (e.touches.length === 2) {
        const [a, b] = e.touches;
        const dx = a.clientX - b.clientX;
        const dy = a.clientY - b.clientY;
        pinchStateRef.current = {
          active: true,
          startDist: Math.hypot(dx, dy),
          startCentroidY: (a.clientY + b.clientY) / 2,
          startScale: gridScale,
          startPanY: gridPanY,
        };
      }
    };
    const onTouchMove = (e) => {
      if (!pinchStateRef.current.active || e.touches.length !== 2) return;
      e.preventDefault();
      const [a, b] = e.touches;
      const dx = a.clientX - b.clientX;
      const dy = a.clientY - b.clientY;
      const dist = Math.hypot(dx, dy);
      const centroidY = (a.clientY + b.clientY) / 2;
      const s = pinchStateRef.current;
      // Pinch component: distance ratio drives scale.
      const ratio = dist / (s.startDist || 1);
      const nextScale = clampScale(s.startScale * ratio);
      setGridScale(+nextScale.toFixed(3));
      // Pan component: centroid Y delta drives translate. Both run in the
      // same gesture so users can pinch-AND-pan simultaneously.
      const nextPan = clampPan(s.startPanY + (centroidY - s.startCentroidY));
      setGridPanY(Math.round(nextPan));
    };
    const onTouchEnd = (e) => {
      if (e.touches.length < 2) pinchStateRef.current.active = false;
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    el.addEventListener('touchcancel', onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [gridScale, gridPanY]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--background-main)' }}>
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#8B5A3C]/30 border-t-[#8B5A3C] rounded-full animate-spin mx-auto mb-4" />
          <p className="text-[var(--text-secondary)]">Loading puzzle...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] overscroll-none" style={{ backgroundColor: 'var(--background-main)' }}>
      <GameHeader
        difficulty={difficulty}
        timeDisplay={timeDisplay}
        isPaused={isPaused}
        onBack={handleBack}
        onTogglePause={togglePause}
        theme={theme}
        onToggleTheme={toggleTheme}
      />

      <main className="game-layout" data-highlight-style={prefs.highlightStyle} data-numpad-side={prefs.numpadSide || 'right'} style={{ '--highlight-rgb': highlightRGB, '--grid-scale': gridScale, '--grid-pan-y': `${gridPanY}px` }}>
        {/* Wisdom sidebar — visible on desktop/laptop only */}
        {!isPaused && (
          <div className="game-wisdom-sidebar">
            <div className="mb-4 pb-4 border-b border-[var(--border-subtle)]" data-testid="sidebar-level-title">
              <h2 className="font-heading text-2xl font-bold text-[var(--text-primary)] leading-tight">
                {difficulty.name}
              </h2>
              <p className="text-xs text-[var(--text-disabled)] mt-0.5">Level {difficulty.level}</p>
              {/* Logic-verified trust signal — every puzzle served has
                  passed solve_with_logic before it left the backend.
                  Subtle chip, not a marketing shout. */}
              <div
                className="mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wide bg-[#6E9B5A]/12 text-[#6E9B5A] border border-[#6E9B5A]/35"
                data-testid="logic-verified-badge"
                title="This puzzle is guaranteed solvable using pure human-solver techniques only — no guessing required."
              >
                <ShieldCheck className="w-3 h-3" />
                <span>Logic-verified</span>
              </div>
              {series.index && series.total ? (
                <div
                  className="mt-2 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wide bg-[var(--accent-blue,#8B5A3C)]/10 text-[var(--accent-blue,#8B5A3C)] border border-[var(--accent-blue,#8B5A3C)]/30"
                  data-testid="series-chip"
                  title="This level has a curriculum: lessons start with the pure technique and progressively layer in prior techniques. Jump around freely."
                >
                  <span>Lesson {series.index} of {series.total}</span>
                </div>
              ) : null}
              {curated.admin ? (
                <div
                  className="mt-2 ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wide bg-[#FFD60A]/15 text-[#FFD60A] border border-[#FFD60A]/40"
                  data-testid="curated-badge"
                  title={
                    curated.label
                      ? `Curated by Sudoku Academy · source: ${curated.label}`
                      : 'Hand-curated puzzle from the Sudoku Academy team.'
                  }
                >
                  <Star className="w-3 h-3" />
                  <span>{curatedBadgeText(curated.label)}</span>
                </div>
              ) : null}
            </div>
            {threadOpen && helpReq ? (
              <HelpThreadPanel req={helpReq} onClose={() => setThreadOpen(false)} onChange={loadHelp} className="max-h-[60vh] mt-2" />
            ) : (
              <WisdomTidbit fact={wisdomFact} fading={wisdomFading} />
            )}
          </div>
        )}

        <div className="game-grid-area" ref={gridAreaRef}>
          {isPaused && !walkthroughActive ? (
            <PausedOverlay onResume={togglePause} />
          ) : (
            <div className="relative">
              {!walkthroughActive && <PanHint gameKey={puzzleId} />}
              {helpReq?.unread > 0 && !threadOpen && (
                <button
                  onClick={() => setThreadOpen(true)}
                  className="absolute left-1/2 -translate-x-1/2 -top-8 z-30 flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold shadow-lg bg-[#8B5A3C] text-[#FFF3E4] whitespace-nowrap animate-in fade-in slide-in-from-top-1"
                  data-testid="help-reply-banner"
                >
                  <LifeBuoy className="w-3.5 h-3.5" />
                  {helpReq.unread === 1 ? 'Someone replied to your help request' : `${helpReq.unread} new replies to your help request`}
                </button>
              )}
              <SudokuBoard
                grid={walkthroughActive && walkthroughGrid ? walkthroughGrid : grid}
                originalGrid={originalGrid}
                pencilMarks={walkthroughActive ? walkthroughPencilMarks : pencilMarks}
                errors={walkthroughActive ? [] : displayErrors}
                onCellClick={walkthroughActive ? () => {} : selectCell}
                isSelected={walkthroughActive ? isWalkthroughSelected : isSelected}
                isRelated={walkthroughActive ? isWalkthroughRelated : isRelated}
                isSameNumber={walkthroughActive ? () => false : isSameNumber}
                isUnrelated={walkthroughActive ? () => false : isUnrelated}
                focusIntensity={focusIntensity}
                answerStyle={prefs.answerStyle}
                answerColorValue={answerColorValue}
                pendingPlacement={walkthroughPendingPlacement}
                pendingReferences={walkthroughReferenceCells}
              />
              <ScribbleCanvas
                ref={scribbleRef}
                active={scribbleActive}
                eraserActive={scribbleEraserActive}
                color={scribbleColor}
                onClear={scribbleClearRef}
              />
            </div>
          )}
        </div>

        <div className="game-controls-area">
          {/* Focus, Scribble & Settings toolbar */}
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="mobile-level-pill" data-testid="mobile-level-pill">
              <span className="level-num">L{difficulty.level}</span>
              <span>{difficulty.name}</span>
              {series.index && series.total ? (
                <span className="lesson-pill" data-testid="mobile-lesson-pill">
                  · {series.index}/{series.total}
                </span>
              ) : null}
              {curated.admin ? (
                <span
                  className="lesson-pill ml-1 inline-flex items-center gap-1"
                  data-testid="mobile-curated-pill"
                  title={
                    curated.label
                      ? `Curated · source: ${curated.label}`
                      : 'Hand-curated by Sudoku Academy'
                  }
                  style={{ color: '#FFD60A' }}
                >
                  <Star className="w-3 h-3" />
                  <span>Curated</span>
                </span>
              ) : null}
            </span>

            <button
              onClick={zoomOut}
              disabled={gridScale <= 0.6}
              className="grid-zoom-btn"
              data-testid="grid-zoom-out-btn"
              aria-label="Shrink grid"
              title="Shrink grid"
            >
              <ZoomOut className="w-3.5 h-3.5" />
            </button>
            <span
              className="grid-zoom-level"
              data-testid="grid-zoom-level"
              title="Click to reset to 100%"
              onClick={() => { setGridScale(1); setGridPanY(0); }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { setGridScale(1); setGridPanY(0); } }}
            >
              {Math.round(gridScale * 100)}%
            </span>
            <button
              onClick={zoomIn}
              disabled={gridScale >= 1.8}
              className="grid-zoom-btn"
              data-testid="grid-zoom-in-btn"
              aria-label="Enlarge grid"
              title="Enlarge grid (or scroll / pinch over grid)"
            >
              <ZoomIn className="w-3.5 h-3.5" />
            </button>

            {/* Mobile-only 3-dot toggle for the secondary tools row.
                Hidden on desktop via CSS; on mobile, collapses the
                Focus/Draw/Validate/Solve/Display utilities so the grid
                and numpad fit 100% in the viewport by default. */}
            <button
              onClick={() => setMobileToolsOpen((v) => !v)}
              className={`mobile-tools-toggle ${mobileToolsOpen ? 'is-open' : ''}`}
              data-testid="mobile-tools-toggle"
              aria-label={mobileToolsOpen ? 'Hide tools' : 'Show tools'}
              aria-expanded={mobileToolsOpen}
              title={mobileToolsOpen ? 'Hide tools' : 'Show tools'}
            >
              <MoreHorizontal className="w-4 h-4" />
            </button>

            <div
              className="game-toolbar-utils"
              data-mobile-open={mobileToolsOpen ? 'true' : 'false'}
              data-testid="game-toolbar-utils"
            >

            <button
              onClick={toggleFocusMode}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                focusMode 
                  ? 'bg-[#8B5A3C]/20 text-[#5AC8FA] border border-[#8B5A3C]/40' 
                  : 'bg-[var(--hover-bg-subtle)] text-[var(--text-muted)] border border-[var(--border-subtle)] hover:text-[var(--text-secondary)]'
              }`}
              data-testid="focus-mode-btn"
            >
              <Focus className="w-3.5 h-3.5" />
              Focus
            </button>
            
            {focusMode && (
              <input
                type="range"
                min="0.3"
                max="0.95"
                step="0.05"
                value={focusIntensity}
                onChange={(e) => setFocusIntensity(parseFloat(e.target.value))}
                className="w-20 h-1 accent-[#8B5A3C] opacity-60"
                data-testid="focus-intensity-slider"
              />
            )}

            <button
              onClick={toggleScribble}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                scribbleActive 
                  ? 'bg-[#FF9F0A]/20 text-[#FF9F0A] border border-[#FF9F0A]/40' 
                  : 'bg-[var(--hover-bg-subtle)] text-[var(--text-muted)] border border-[var(--border-subtle)] hover:text-[var(--text-secondary)]'
              }`}
              data-testid="scribble-btn"
            >
              <PenTool className="w-3.5 h-3.5" />
              Draw
            </button>

            {scribbleActive && (
              <>
                {/* Eraser button — only visible when Draw is active. Sits right
                    next to Draw so the pair behaves like a single tool widget. */}
                <button
                  onClick={toggleScribbleEraser}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                    scribbleEraserActive
                      ? 'bg-[#FF9F0A]/20 text-[#FF9F0A] border border-[#FF9F0A]/40'
                      : 'bg-[var(--hover-bg-subtle)] text-[var(--text-muted)] border border-[var(--border-subtle)] hover:text-[var(--text-secondary)]'
                  }`}
                  data-testid="toolbar-erase-btn"
                  title={scribbleEraserActive ? 'Eraser ON — click to switch back to pen' : 'Switch to eraser'}
                >
                  <Eraser className="w-3.5 h-3.5" />
                  Erase
                </button>
                {/* Color picker — appears next to Draw when active */}
                <div className="flex items-center gap-1 px-1" data-testid="scribble-color-picker">
                  {[
                    { value: '#0094FF', label: 'Blue' },
                    { value: '#FF453A', label: 'Red' },
                    { value: '#34C759', label: 'Green' },
                    { value: '#FF9F0A', label: 'Orange' },
                    { value: '#AF52DE', label: 'Purple' },
                    { value: '#1A1A2E', label: 'Black' },
                  ].map(c => (
                    <button
                      key={c.value}
                      onClick={() => { setScribbleColor(c.value); setScribbleEraserActive(false); }}
                      title={c.label}
                      data-testid={`scribble-color-${c.label.toLowerCase()}`}
                      className={`rounded-full transition-all ${
                        scribbleColor === c.value && !scribbleEraserActive
                          ? 'w-5 h-5 ring-2 ring-offset-1 ring-[var(--text-primary)] ring-offset-[var(--background-surface)]'
                          : 'w-4 h-4 hover:scale-110'
                      }`}
                      style={{ backgroundColor: c.value }}
                      aria-label={`Pen color: ${c.label}`}
                    />
                  ))}
                </div>
                <button
                  onClick={clearScribble}
                  className="flex items-center gap-1 px-2 py-1.5 rounded text-xs text-[#FF453A] hover:bg-[#FF453A]/10 transition-colors"
                  data-testid="clear-scribble-btn"
                >
                  <Trash2 className="w-3 h-3" />
                  Clear
                </button>
              </>
            )}

            {!walkthroughActive && !gameComplete && (
              <>
                <button
                  onClick={() => handleValidate(false)}
                  disabled={validating}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                    validationIssue
                      ? 'bg-[#FF9F0A]/20 text-[#FF9F0A] border border-[#FF9F0A]/50 animate-pulse'
                      : 'bg-[var(--hover-bg-subtle)] text-[var(--text-muted)] border border-[var(--border-subtle)] hover:text-[var(--text-secondary)]'
                  } disabled:opacity-40`}
                  data-testid="validate-btn"
                  title={validationIssue
                    ? 'Possible error detected — click to see which entry is likely wrong'
                    : 'Check whether your current grid has a hidden contradiction'}
                >
                  <ShieldCheck className="w-3.5 h-3.5" />
                  {validating ? 'Checking…' : 'Validate'}
                </button>
                <button
                  onClick={handleCheckWork}
                  disabled={checkingWork}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors bg-[var(--hover-bg-subtle)] text-[var(--text-muted)] border border-[var(--border-subtle)] hover:text-[var(--text-secondary)] disabled:opacity-40"
                  data-testid="check-work-btn"
                  title="Compare your current entries against the solution and highlight any that are wrong"
                >
                  <ShieldCheck className="w-3.5 h-3.5" />
                  {checkingWork ? 'Checking…' : 'Check My Work'}
                </button>
                {wrongCells.length > 0 && (
                  <button
                    onClick={clearWrongCells}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors bg-[#FF3B30]/15 text-[#FF3B30] border border-[#FF3B30]/40 hover:bg-[#FF3B30]/25"
                    data-testid="clear-wrong-btn"
                    title="Erase every cell currently highlighted as wrong. A single Undo restores them."
                  >
                    <Eraser className="w-3.5 h-3.5" />
                    Clear Wrong ({wrongCells.length})
                  </button>
                )}
                <button
                  onClick={startWalkthrough}
                  disabled={walkthroughLoading}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors bg-[var(--hover-bg-subtle)] text-[var(--text-muted)] border border-[var(--border-subtle)] hover:text-[var(--text-secondary)] disabled:opacity-40"
                  data-testid="walkthrough-btn"
                  title="Watch the puzzle solved step-by-step"
                >
                  <BookOpen className="w-3.5 h-3.5" />
                  {walkthroughLoading ? 'Loading...' : 'Solve'}
                </button>
                <button
                  onClick={helpReq?.status === 'open' ? () => setThreadOpen(true) : handleStuck}
                  className={`relative flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                    helpReq?.status === 'open'
                      ? 'bg-[#8B5A3C]/15 text-[#8B5A3C] border border-[#8B5A3C]/40'
                      : 'bg-[var(--hover-bg-subtle)] text-[var(--text-muted)] border border-[var(--border-subtle)] hover:text-[var(--text-secondary)]'
                  }`}
                  data-testid="stuck-help-btn"
                  title={helpReq?.status === 'open' ? 'See community replies' : 'Stuck? Check your work, get the next step, or ask the community'}
                >
                  <LifeBuoy className="w-3.5 h-3.5" />
                  {helpReq?.status === 'open' ? 'Help thread' : 'Stuck?'}
                  {helpReq?.unread > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-[#FF3B30] text-white text-[10px] leading-4 text-center" data-testid="stuck-unread-badge">{helpReq.unread}</span>
                  )}
                </button>
                {difficulty.level <= 4 && (
                  <button
                    onClick={() => navigate(`/tutorials/${difficulty.level}`)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors bg-[#34C759]/10 text-[#34C759] border border-[#34C759]/30 hover:bg-[#34C759]/20"
                    data-testid="tutorial-link-btn"
                    title="Learn this technique"
                  >
                    <BookOpen className="w-3.5 h-3.5" />
                    Learn
                  </button>
                )}
              </>
            )}

            <button
              onClick={() => setSettingsOpen(prev => !prev)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                settingsOpen 
                  ? 'bg-[var(--accent-primary)]/20 text-[var(--accent-primary)] border border-[var(--accent-primary)]/40' 
                  : 'bg-[var(--hover-bg-subtle)] text-[var(--text-muted)] border border-[var(--border-subtle)] hover:text-[var(--text-secondary)]'
              }`}
              data-testid="settings-btn"
            >
              <Settings2 className="w-3.5 h-3.5" />
              Display
            </button>
            </div>
          </div>

          {settingsOpen && (
            <GameSettingsPanel onClose={() => setSettingsOpen(false)} />
          )}

          {walkthroughActive && (
            <WalkthroughPanel
              steps={walkthroughSteps}
              currentIndex={walkthroughIndex}
              onPrev={walkthroughPrev}
              onNext={walkthroughNext}
              onExit={stopWalkthrough}
              autoPlaying={autoPlaying}
              onToggleAutoPlay={toggleAutoPlay}
              userTier={user?.subscription_tier || 'free'}
              difficultyLevel={difficulty.level}
              onUpgrade={() => navigate('/subscription')}
            />
          )}

          {!walkthroughActive && (
            <>
              <Numpad
                onNumberClick={handleNumberClick}
                onErase={toggleScribbleEraser}
                onToggleNotes={handleToggleNotes}
                onHint={handleHint}
                onAutoFill={autoFillCandidates}
                onUndo={handleUndo}
                onRedo={handleRedo}
                onToggleScribble={toggleScribble}
                scribbleActive={scribbleActive}
                scribbleEraserActive={scribbleEraserActive}
                noteMode={noteMode}
              />

              {/* Desktop (≥1024px): keep the pre-existing 2-button row
                  [Auto-candidates · Reset] — the numpad already carries
                  Undo/Redo/Hint below the 3×3 digits, and Draw lives in
                  the top toolbar. No point duplicating them here. */}
              <div className="hidden lg:flex gap-2 items-stretch" data-testid="row-desktop-below">
                <button
                  onClick={autoFillCandidates}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-[var(--background-surface)] hover:bg-[var(--hover-bg)] border border-[var(--border-subtle)] text-[var(--text-primary)] transition-colors"
                  title="Fill every empty cell with its legal candidates"
                  data-testid="auto-candidates-btn"
                >
                  <Check className="w-4 h-4 text-[#5AC8FA]" />
                  <span className="whitespace-nowrap">Auto-candidates</span>
                </button>
                <button
                  onClick={() => setResetDialogOpen(true)}
                  className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-sm font-medium bg-[var(--background-surface)] hover:bg-[#FF3B30]/10 border border-[var(--border-subtle)] hover:border-[#FF3B30]/40 text-[var(--text-secondary)] hover:text-[#FF453A] transition-colors"
                  data-testid="reset-btn"
                  title="Reset puzzle or scribble"
                >
                  <RotateCcw className="w-4 h-4" />
                  Reset
                </button>
              </div>

              {/* Mobile portrait + landscape: 2-row layout requested by the
                  user. Hidden on desktop where the numpad already carries
                  Undo/Redo/Hint natively. */}
              <div className="flex gap-2 items-stretch below-numpad-row lg:hidden" data-testid="row-autocandidates-undo">
                <button
                  onClick={autoFillCandidates}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-[var(--background-surface)] hover:bg-[var(--hover-bg)] border border-[var(--border-subtle)] text-[var(--text-primary)] transition-colors"
                  title="Fill every empty cell with its legal candidates"
                  data-testid="auto-candidates-btn-mobile"
                >
                  <Check className="w-4 h-4 text-[#5AC8FA]" />
                  <span className="whitespace-nowrap">Auto-candidates</span>
                </button>
                <button
                  onClick={handleUndo}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium bg-[var(--background-surface)] hover:bg-[var(--hover-bg)] border border-[var(--border-subtle)] text-[var(--text-primary)] transition-colors"
                  data-testid="below-undo-btn"
                  title="Undo last move"
                >
                  <Undo2 className="w-4 h-4" />
                  Undo
                </button>
              </div>

              {/* "Fabulous" 4-button row — Reset · Draw · Hint · Redo. */}
              <div className="grid grid-cols-4 gap-2 below-numpad-row lg:hidden" data-testid="row-reset-draw-hint-redo">
                <button
                  onClick={() => setResetDialogOpen(true)}
                  className="flex items-center justify-center gap-1.5 px-2 py-2 rounded-md text-sm font-medium bg-[var(--background-surface)] hover:bg-[#FF3B30]/10 border border-[var(--border-subtle)] hover:border-[#FF3B30]/40 text-[var(--text-secondary)] hover:text-[#FF453A] transition-colors"
                  data-testid="reset-btn-mobile"
                  title="Reset puzzle or scribble"
                >
                  <RotateCcw className="w-4 h-4" />
                  <span className="below-row-label">Reset</span>
                </button>
                <button
                  onClick={toggleScribble}
                  className={`flex items-center justify-center gap-1.5 px-2 py-2 rounded-md text-sm font-medium border transition-colors ${
                    scribbleActive
                      ? 'bg-[#FF9F0A]/20 text-[#FF9F0A] border-[#FF9F0A]/40'
                      : 'bg-[var(--background-surface)] hover:bg-[var(--hover-bg)] border-[var(--border-subtle)] text-[var(--text-primary)]'
                  }`}
                  data-testid="below-draw-btn"
                  title="Toggle scribble mode"
                >
                  <PenTool className="w-4 h-4" />
                  <span className="below-row-label">Draw</span>
                </button>
                <button
                  onClick={handleHint}
                  className="flex items-center justify-center gap-1.5 px-2 py-2 rounded-md text-sm font-medium bg-[var(--background-surface)] hover:bg-[var(--hover-bg)] border border-[var(--border-subtle)] text-[var(--text-primary)] transition-colors"
                  data-testid="below-hint-btn"
                  title="Show next logical hint"
                >
                  <Lightbulb className="w-4 h-4" />
                  <span className="below-row-label">Hint</span>
                </button>
                <button
                  onClick={handleRedo}
                  className="flex items-center justify-center gap-1.5 px-2 py-2 rounded-md text-sm font-medium bg-[var(--background-surface)] hover:bg-[var(--hover-bg)] border border-[var(--border-subtle)] text-[var(--text-primary)] transition-colors"
                  data-testid="below-redo-btn"
                  title="Redo last undone move"
                >
                  <Redo2 className="w-4 h-4" />
                  <span className="below-row-label">Redo</span>
                </button>
              </div>

              <AlertDialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
                <AlertDialogContent data-testid="reset-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle>What would you like to reset?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Resetting the puzzle will clear all your answers and candidates. Resetting the scribble only clears your drawings. This cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                    <AlertDialogCancel data-testid="reset-cancel">Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleResetScribbleOnly}
                      className="bg-[var(--background-elevated)] hover:bg-[var(--hover-bg)] text-[var(--text-primary)] border border-[var(--border-subtle)]"
                      data-testid="reset-scribble-only"
                    >
                      Reset scribble only
                    </AlertDialogAction>
                    <AlertDialogAction
                      onClick={handleResetPuzzle}
                      className="bg-[#FF3B30] hover:bg-[#FF453A] text-white"
                      data-testid="reset-puzzle-confirm"
                    >
                      Reset entire puzzle
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          )}

          {gameComplete && (
            <CompletionCard
              timeDisplay={timeDisplay}
              onShare={handleShare}
              onNewPuzzle={handleBack}
            />
          )}

          {/* Below-grid ad — only renders when admin flips ads_enabled on.
              Sized to 300×250 (medium rectangle) so it slots nicely under
              the puzzle on mobile and beside it on desktop. */}
          <AdSlot size="medium-rectangle" testId="ad-game-below" />
        </div>
      </main>

      <StuckDialog
        open={stuckOpen}
        onOpenChange={setStuckOpen}
        puzzleId={puzzleId}
        grid={grid}
        pencilMarks={pencilMarks}
        user={user}
        helpRequest={helpReq}
        onShowWrong={(cells) => {
          setWrongCells(cells.map((w) => [w.row, w.col]));
          if (cells[0]) selectCell(cells[0].row, cells[0].col);
        }}
        onHint={handleHint}
        onRequestCreated={(req) => { setHelpReq(req); setStuckOpen(false); setThreadOpen(true); }}
        onOpenThread={() => setThreadOpen(true)}
      />
      {threadOpen && helpReq && (
        <div className="help-thread-mobile fixed inset-x-2 bottom-2 z-40 max-h-[42dvh]">
          <HelpThreadPanel req={helpReq} onClose={() => setThreadOpen(false)} onChange={loadHelp} className="w-full max-h-[42dvh]" />
        </div>
      )}

      {unlockedAchievements && unlockedAchievements.length > 0 && (
        <UnlockModal
          achievements={unlockedAchievements}
          onClose={() => setUnlockedAchievements(null)}
        />
      )}
    </div>
  );
}
